import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Flame, Shield, Zap, Calendar, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";

function buildDays() {
  const days = [];
  const today = new Date();
  for (let i = 83; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  // chunk into 12 week-columns of 7
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

export default function Streak() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const weeks = buildDays();
  const todayStr = new Date().toISOString().slice(0, 10);

  const load = () => api.get("/streak").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const buy = async () => {
    setBusy(true);
    try {
      const res = await api.post("/streak/buy-freeze");
      setData((d) => ({ ...d, freeze_tokens: res.data.freeze_tokens, xp: res.data.xp }));
      toast.success("🛡️ Freeze added to your stash!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not buy freeze."); }
    finally { setBusy(false); }
  };

  const d = data || {};
  const active = new Set(d.active_days || []);
  const frozen = new Set(d.frozen_days || []);
  const canBuy = (d.freeze_tokens ?? 0) < (d.freeze_max ?? 3) && (d.xp ?? 0) >= (d.freeze_cost_xp ?? 100);

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl">Your Streak</h1>
        <p className="text-gray-600">Keep the flame alive — and use freezes to protect it on off days.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="nb-card bg-butter p-5" data-testid="streak-count">
            <Flame className="w-7 h-7" />
            <div className="font-cabinet font-extrabold text-4xl mt-2">{d.streak_count ?? 0}</div>
            <div className="font-bold text-sm">day streak</div>
          </div>
          <div className="nb-card bg-mint p-5">
            <Zap className="w-7 h-7" />
            <div className="font-cabinet font-extrabold text-4xl mt-2">{d.xp ?? 0}</div>
            <div className="font-bold text-sm">total XP</div>
          </div>
          <div className="nb-card bg-sky p-5" data-testid="streak-freezes">
            <div className="flex items-center gap-1">
              {Array.from({ length: d.freeze_max ?? 3 }).map((_, i) => (
                <Shield key={i} className={`w-6 h-6 ${i < (d.freeze_tokens ?? 0) ? "" : "opacity-25"}`} />
              ))}
            </div>
            <div className="font-cabinet font-extrabold text-4xl mt-2">{d.freeze_tokens ?? 0}</div>
            <div className="font-bold text-sm">freezes banked</div>
          </div>
        </div>

        {/* Freeze shop */}
        <div className="nb-card p-5 mt-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="freeze-shop">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl border-2 border-ink bg-sky flex items-center justify-center"><Shield className="w-6 h-6" /></div>
            <div>
              <div className="font-cabinet font-bold text-lg">Buy a Streak Freeze</div>
              <div className="text-sm text-gray-600">Costs {d.freeze_cost_xp ?? 100} XP · protects one missed day (max {d.freeze_max ?? 3})</div>
            </div>
          </div>
          <button onClick={buy} disabled={busy || !canBuy} className="nb-btn-primary px-6 py-3 shrink-0" data-testid="buy-freeze-btn">
            <Plus className="w-5 h-5" /> {(d.freeze_tokens ?? 0) >= (d.freeze_max ?? 3) ? "Max reached" : `Buy (${d.freeze_cost_xp ?? 100} XP)`}
          </button>
        </div>

        {/* Heatmap */}
        <div className="nb-card p-5 mt-6">
          <div className="flex items-center gap-2 mb-4"><Calendar className="w-5 h-5" /><h3 className="font-cabinet font-bold text-xl">Last 12 weeks</h3>
            <span className="ml-auto text-sm text-gray-500">{d.total_active_days ?? 0} active days</span>
          </div>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2" data-testid="streak-heatmap">
            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col gap-1.5">
                {week.map((date) => {
                  const isToday = date === todayStr;
                  const isActive = active.has(date);
                  const isFrozen = frozen.has(date);
                  const bg = isActive ? "bg-mint" : isFrozen ? "bg-sky" : "bg-gray-100";
                  return (
                    <motion.div key={date} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: wi * 0.01 }}
                      title={`${date}${isActive ? " · active" : isFrozen ? " · frozen 🛡️" : ""}`}
                      className={`w-5 h-5 rounded-md border-2 ${isToday ? "border-coral" : "border-ink/20"} ${bg} flex items-center justify-center`}>
                      {isFrozen && <Shield className="w-3 h-3 text-ink/60" />}
                    </motion.div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-4 text-xs font-bold text-gray-500">
            <span className="flex items-center gap-1"><span className="w-4 h-4 rounded border-2 border-ink/20 bg-mint" /> Active</span>
            <span className="flex items-center gap-1"><span className="w-4 h-4 rounded border-2 border-ink/20 bg-sky" /> Frozen</span>
            <span className="flex items-center gap-1"><span className="w-4 h-4 rounded border-2 border-coral" /> Today</span>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
